import { Component, DestroyRef, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { AppConfig } from '@app/core/app.config';
import { PermissionEnum } from '@app/core/enums/permissions.enum';
import { CartovistaParameters } from '@app/core/model/entities/external-api/cartovista-parameters';
import { TranslateService } from '@ngx-translate/core';
import { GisService } from '@services/gis.service';
import { AppManager } from '@services/managers/app.manager';
import { PopupManager } from '@services/managers/popup.manager';
import { SnackbarManager } from '@services/managers/snackbar.manager';
import { tap } from 'rxjs/operators';

@Component({
    selector: 'cartovista',
    templateUrl: './cartovista.component.html',
    styleUrl: './cartovista.component.scss',
    standalone: false
})
export class CartovistaComponent implements OnInit {

  protected readonly Permission = PermissionEnum;

  @ViewChild('cartovista') public cartovista: ElementRef<HTMLIFrameElement>;

  protected cartovistaParameters: CartovistaParameters;
  protected cartovistaPanelContent: SafeResourceUrl;

  protected appConfig: AppConfig = inject(AppConfig);
  private gisService: GisService = inject(GisService);
  private appManager: AppManager = inject(AppManager);
  private sanitizer: DomSanitizer = inject(DomSanitizer);
  private translate: TranslateService = inject(TranslateService);
  private snackbarManager: SnackbarManager = inject(SnackbarManager);
  private popupManager: PopupManager = inject(PopupManager);
  private destroyRef = inject(DestroyRef);

  /**
   * Init and load Cartovista parameters
   */
  public ngOnInit(): void {
    this.gisService.getCartovistaParameters(this.appManager.currentOrganization.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.cartovistaParameters = data;
        },
        complete: () => {
          if (this.cartovistaParameters.areApiKeysSet) {
            this.updateMapUrl();
          }
        }
      });
  }

  /**
   * Using the Cartovista service API, synchronises the data with the cartographic layer
   */
  public refreshData(): void {
    this.gisService.refreshCartovistaData()
      .pipe(
        tap((it) => {
          if (it.status === 'INVALID') throw new Error(it.failMessage);
          console.debug(`Refresh window #${it.windowId}`);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        error: () => {
          this.popupManager.showOkPopup({
            dialogTitle: this.translate.instant('TITLE.ERROR_REFRESH_CARTOVISTA'),
            dialogMessage: this.translate.instant('MESSAGE.ERROR_REFRESH_CARTOVISTA'),
            type: 'error'
          });
        },
        complete: () => {
          this.updateMapUrl();
          this.snackbarManager.showActionSnackbar(this.translate.instant('MESSAGE.REFRESH_CARTOVISTA'));
        }
      });
  }

  /**
   * Updates the Cartovista embed URL
   */
  private updateMapUrl(): void {
    if (this.cartovistaParameters.areApiKeysSet) {
      const mapUrl = `${this.cartovistaParameters.serverUrl}/${this.cartovistaParameters.tenantCode}/maps/${this.cartovistaParameters.mapUrl}?embed=1&topbarcollapsed&sidebarcollapsed&apiKey=${this.cartovistaParameters.apiKey}`;
      this.cartovistaPanelContent = this.sanitizer.bypassSecurityTrustResourceUrl(mapUrl);
    }
  }
}
